
import { computed, defineComponent, onBeforeMount, PropType, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import AddCourierModal from '@/modules/TMSModule/components/AddCourierModal/index.vue'
import AddCarModal from '@/modules/TMSModule/components/AddCarModal/index.vue'
import iconRefresh from '@/assets/icons/refresh.svg'
import NewModal from '@/common/new-components/NewModal/index.vue'

// realisations
import { carriers } from '@/core/realisations/carriers.realisation'
import { couriers } from '@/core/realisations/couriers.realisation'
import { security } from '@/core/realisations/security.realisation'
import { cities } from '@/core/realisations/cities.realisation'
import { contracts } from '@/core/realisations/contracts.realisation'
import couriersAPI from '@/new-services/couriers.service'
import tmsAPI from '@/new-services/tms.service'

// classes
import { DataState } from '@/core/classes/DataState.class'

// types
import { CarrierT } from '@/core/types/carriers.types'
import { CouirerCarT, CreateCouirerCar, DispatcherCourierT } from '@/core/types/couriers.types'
import {
    DoDriverSearchData,
    DoDriverSearchParamsT,
    SecurityGetCheckupStatusResponseT,
} from '@/core/types/security.types'
import { CityT } from '@/core/types/cities.types'

// validation
import useVuelidate from '@vuelidate/core'
import { DoDriverSearchValidation } from '@/modules/BidsModule/validations/AddBid.validation'
import { required, requiredIf, integer } from '@vuelidate/validators'

// composable
import useNotifications from '@/common/composable/useNotifications'
import { useFormData } from '@/common/composable/useFormData'
import { CompanyT } from '@/core/types/companies.types'
import { companies } from '@/core/realisations/companies.realisation'
import { Courier } from '@/new-services/types/couriers'
import { CheckCostParams } from '@/new-services/types/tms'
import { FTL } from '@/core/realisations/FTL.realisation'
import { FullFTLOrderT } from '@/core/types/orders.types'
import { PHONE_NUMBER_MASK } from '@/core/constants/masks.constants'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        TheSelect,
        TheInput,
        FileReader,
        Checkbox,
        AddCourierModal,
        AddCarModal,
        iconRefresh,
        NewModal,
        TheTextarea,
    },
    props: {
        order: {
            type: Object as PropType<FullFTLOrderT>,
            required: true,
        },
    },
    emits: ['cancel', 'added'],
    setup(props, { emit }) {
        const notifications = useNotifications()
        const { getFormData } = useFormData()

        const state = reactive({
            is_loading: false,
            //
            is_cost: false,
            cost: null as number | null,
            direction: null as string | null,
            //
            comment: null as string | null,
            carCapacityId: null as number | null,
            //
            carrierId: null as number | null,
            carId: null as number | null,
            cityIds: [null, null] as (number | null)[],
            senderCompanyId: 91,
            receiverCompanyId: 91,
            dateOfLoad: null as string | null,
            dateOfUnload: null as string | null,
            senderFullName: null as string | null,
            receiverFullName: null as string | null,
            senderPhoneNumber: null as string | null,
            receiverPhoneNumber: null as string | null,
            loadAddress: null as string | null,
            unloadAddress: null as string | null,
            natureOfCargo: null as string | null,
            insurance: null as string | null,
            bodyType: null as string | null,
            temperatureRegime: null as string | null,
            costOfTransportation: null as string | null,
            courier_id: null as number | null,
            identifyCard: null as string | null,
            identifyCardDate: null as string | null,
            licence: null as string | null,
            licenceDate: null as string | null,
            brand: null as string | null,
            trailerLicenseNumber: null as string | null,
            type: null as string | null,
            technicalPassport: null as string | null,
            iin: null as string | null,
            full_name: null as string | null,
            vehicleOwnerFullName: null as string | null,
            number: null as string | null,
            phone_number: null as string | null,
            carTechnicalPassportFiles: [] as File[],
            isTripartite: false,
            tripartiteNumber: null as string | null,
            tripartiteDate: null as string | null,
            isNds: 0,
        })

        const sb_state_iin = reactive({
            is_checked: false,
            is_rejected: null as boolean | null,
            is_loading: false,
            reject_comment: null as string | null,
            is_request_completed: false,
            //
            is_success: false,
        })

        const courier_state = reactive({
            is_opened: false,
            is_loading: false,
            is_not_found: false,
        })

        const car_state = reactive({
            is_opened: false,
            is_loading: false,
            is_not_found: false,
        })

        const sb_state_car = reactive({
            is_checked: false,
            is_rejected: null as boolean | null,
            is_loading: false,
            reject_comment: null as string | null,
            is_request_completed: false,
            //
            is_success: false,
        })

        const contract_state = reactive({
            is_loading: false,
        })

        const AddBidValidation = {
            courier_id: { required },
            carrierId: { required, integer },
            senderCompanyId: { required, integer },
            receiverCompanyId: { required, integer },
            dateOfLoad: { required },
            dateOfUnload: { required },
            senderFullName: { required },
            receiverFullName: { required },
            //
            loadAddress: { required },
            senderPhoneNumber: { required },
            unloadAddress: { required },
            receiverPhoneNumber: { required },
            natureOfCargo: { required },
            insurance: { required },
            bodyType: { required },
            temperatureRegime: { required },
            identifyCard: { required },
            identifyCardDate: { required },
            licence: { required },
            licenceDate: { required },
            //
            iin: { required },
            full_name: { required },
            vehicleOwnerFullName: { required },
            number: { required },
            phone_number: { required },
            carTechnicalPassportFiles: { required },
            carId: { required },
            //
            cityIds: {
                $each: {
                    required,
                },
            },
            //
            tripartiteNumber: { required: requiredIf(() => state.isTripartite) },
            tripartiteDate: { required: requiredIf(() => state.isTripartite) },
        }

        // @ts-ignore
        const state_form$ = useVuelidate(AddBidValidation, state)
        const iin_form$ = useVuelidate(DoDriverSearchValidation, state)

        const data_state = reactive({
            carriers: new DataState<CarrierT>({}),
            companies: new DataState<CompanyT>({}),
            couriers: new DataState<Courier>({}),
            cities: new DataState<CityT>({}),
            cars: new DataState<CouirerCarT>({}),
        })

        const termAndFormOfPayment = 'Согласно договору'

        const loadContract = async (carrier_bin: string) => {
            try {
                contract_state.is_loading = true

                const response = await contracts.getContractByBin(carrier_bin)

                state.isTripartite = true
                state.tripartiteNumber = response.data.number
                state.tripartiteDate = response.data.date
            } catch (error) {
                console.error(error)
            } finally {
                contract_state.is_loading = false
            }
        }

        const loadCompanies = async () => {
            try {
                data_state.companies.is_loading = true

                const response = await companies.getAllList({
                    page: 1,
                    getAll: 1,
                    isExtended: 0,
                    limit: 9999,
                    statusId: 2,
                })
                data_state.companies.list = response.data

                data_state.companies.is_failed = false
            } catch (error) {
                data_state.companies.is_failed = true
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При получении списка произошла ошибка',
                })
            } finally {
                data_state.companies.is_loading = false
            }
        }

        // Перевозчики

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true
                const response = await carriers.getList({ page, search, statusId: 2, isExtended: 0 })
                data_state.carriers.list = response.data

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                data_state.carriers.is_loading = true

                const response = await carriers.getList({ page, statusId: 2, isExtended: 0 })
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        // Курьеры

        const loadCouriers = async (page: number, name?: string) => {
            if (!state.carrierId) return

            data_state.couriers.pagination.current_page = page

            try {
                data_state.couriers.is_loading = true

                const params = {
                    page,
                    limit: 9999,
                    statusIds: [2],
                    typesIds: [2],
                    name,
                }

                const response = await couriersAPI.getCouriers(params)
                data_state.couriers.list = response.data.data

                data_state.couriers.pagination.total_pages = response.data.meta.last_page

                data_state.couriers.is_failed = false
            } catch (error) {
                data_state.couriers.is_failed = true
                console.error(error)
            } finally {
                data_state.couriers.is_loading = false
            }
        }

        const searchCouriers = (name: string) => {
            loadCouriers(1, name)
        }

        const unshiftFindedCourier = async (courier: Courier) => {
            await loadCouriers(data_state.couriers.pagination.current_page)
            data_state.couriers.list.unshift(courier)
        }

        //

        const resetSbStateIIN = () => {
            sb_state_iin.is_checked = false
            sb_state_iin.is_rejected = null
            sb_state_iin.reject_comment = null
            sb_state_iin.is_request_completed = false
            sb_state_iin.is_success = false
        }

        const resetSbStateCar = () => {
            sb_state_car.is_checked = false
            sb_state_car.is_rejected = null
            sb_state_car.reject_comment = null
            sb_state_car.is_request_completed = false
            sb_state_car.is_success = false
        }

        const isValidIIN = computed(() => {
            return !sb_state_iin.is_loading && state.iin && state.iin.length >= 12
        })

        const checkSbStatusIIN = async () => {
            if (!isValidIIN.value) return
            resetSbStateIIN()

            try {
                sb_state_iin.is_loading = true

                const PERSON_CHECKUP_TYPE = 2

                const response: any = await security.getCheckupStatus({
                    identifier: state.iin as string,
                    type: PERSON_CHECKUP_TYPE,
                })

                if (!response.success) {
                    notifications.notify({
                        title: response.message || 'При проверке произошла ошибка',
                        type: 'danger',
                    })
                }
                sb_state_iin.is_checked = true

                if (response.data.isAccepted === false) {
                    sb_state_iin.is_rejected = true
                    sb_state_iin.reject_comment = response.data.comment
                } else if (response.data.isAccepted === true) {
                    sb_state_iin.is_success = true
                    sb_state_iin.is_rejected = false
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При проверке статуса СБ произошла ошибка',
                })

                sb_state_iin.is_checked = false
            } finally {
                sb_state_iin.is_loading = false
                sb_state_iin.is_request_completed = true
            }
        }

        const checkSbStatusCar = async () => {
            if (sb_state_car.is_loading || !state.number) return
            resetSbStateCar()

            try {
                sb_state_car.is_loading = true

                const AUTO_CHECKUP_TYPE = 3

                const response: SecurityGetCheckupStatusResponseT = await security.getCheckupStatus(
                    {
                        identifier: state.number as string,
                        type: AUTO_CHECKUP_TYPE,
                    }
                )

                sb_state_car.is_checked = true

                if (!response.data.isAccepted) {
                    sb_state_car.is_rejected = true
                    sb_state_car.reject_comment = response.data.comment

                    notifications.notify({
                        title: response.message || 'При проверке произошла ошибка',
                        type: 'danger',
                    })
                } else {
                    sb_state_car.is_success = true
                }
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При проверке статуса СБ произошла ошибка',
                })

                sb_state_car.is_checked = false
            } finally {
                sb_state_car.is_loading = false
                sb_state_car.is_request_completed = true
            }
        }

        const doChecks = async () => {
            if (!isValidIIN.value) {
                notifications.notify({
                    title: 'ИИН должен быть больше 12 символов!',
                    type: 'danger',
                })
                return
            }

            await checkSbStatusIIN()
            await checkSbStatusCar()
        }

        const getPayload = () => {
            let driver = {
                id: state.courier_id,
                iin: state.iin,
                fullName: state.full_name,
                vehicleOwnerFullName: state.vehicleOwnerFullName,
                phone: state.phone_number,
                //
                identifyCard: state.identifyCard,
                identifyCardDate: state.identifyCardDate,
                licence: state.licence,
                licenceDate: state.licenceDate,
            }

            let car = {
                number: state.number,
                technicalPassport: state.technicalPassport,
                //
                brand: state.brand,
                trailerLicenseNumber: state.trailerLicenseNumber,
                type: state.type,
                carCapacityId: state.carCapacityId,
            }

            const payload: any = {
                driverId: state.courier_id,
                carId: state.carId,
                carrierId: state.carrierId,
                senderCompanyId: state.senderCompanyId,
                receiverCompanyId: state.receiverCompanyId,
                dateOfLoad: state.dateOfLoad,
                dateOfUnload: state.dateOfUnload,
                senderFullName: state.senderFullName,
                receiverFullName: state.receiverFullName,
                senderPhoneNumber: state.senderPhoneNumber,
                receiverPhoneNumber: state.receiverPhoneNumber,
                loadAddress: state.loadAddress,
                unloadAddress: state.unloadAddress,
                natureOfCargo: state.natureOfCargo,
                insurance: state.insurance,
                bodyType: state.bodyType,
                temperatureRegime: state.temperatureRegime,
                costOfTransportation: state.costOfTransportation,
                driver,
                car,
                carTechnicalPassportFiles: state.carTechnicalPassportFiles,
                cityIds: state.cityIds,
                isNds: state.isNds,
                // hardcode
                termAndFormOfPayment,
                paymentRateTypeId: 2,
            }

            if (state.isTripartite) {
                payload.tripartiteNumber = state.tripartiteNumber
                payload.tripartiteDate = state.tripartiteDate
            }

            return payload
        }

        const addBid = async () => {
            try {
                state.is_loading = true
                let payload = getPayload()

                if (!payload) return

                await FTL.createLogisticBid(props.order.id, getFormData(payload))
                notifications.notify({ title: 'Заявка успешно создана!', type: 'success' })
                emit('added')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При создании заявки произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const onAddBid = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            await doChecks()
            if (!sb_state_iin.is_success || !sb_state_car.is_success || !props.order) return

            try {
                state.is_loading = true
                let payload = getPayload()

                if (!payload) return

                const params: CheckCostParams = {
                    cityIds: props.order.city_ids,
                    cost: Number(state.costOfTransportation),
                    carCapacityId: Number(state.carCapacityId),
                    invoiceType: 2,
                    isNds: state.isNds
                }
                const responseCost: any = await tmsAPI.checkCost(params)

                if (!responseCost.data.data.isSuccess && responseCost.data.data.statusId === 15) {
                    state.is_cost = true
                    state.cost = responseCost.data.data.cost
                    state.direction = responseCost.data.data.direction

                    return
                }

                await addBid()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При создании заявки произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const resetForm = () => {
            state.carrierId = null
            state.senderCompanyId = 91
            state.receiverCompanyId = 91
            state.dateOfLoad = null
            state.dateOfUnload = null
            state.senderFullName = null
            state.receiverFullName = null
            state.senderPhoneNumber = null
            state.receiverPhoneNumber = null
            //
            state.loadAddress = null
            state.unloadAddress = null
            state.natureOfCargo = null
            state.insurance = null
            state.bodyType = null
            state.temperatureRegime = null
            state.costOfTransportation = null
            //
            state.courier_id = null
            state.identifyCard = null
            state.identifyCardDate = null
            state.licence = null
            state.licenceDate = null
            state.technicalPassport = null
            //
            state.brand = null
            state.trailerLicenseNumber = null
            state.type = null
            //
            state.iin = null
            state.full_name = null
            state.vehicleOwnerFullName = null
            state.number = null
            state.phone_number = null
            //
            state.isTripartite = false
            state.tripartiteNumber = null
            state.tripartiteDate = null
            //
            state.carTechnicalPassportFiles = []
            state.cityIds = [null, null]
            state.isNds = 0

            state_form$.value.$reset()
        }

        const loadCities = async (page: number, search?: string) => {
            data_state.cities.pagination.current_page = page

            try {
                data_state.cities.is_loading = true

                const data = await cities.getAllList(page, search, 9999)
                data_state.cities.list = data.data

                data_state.cities.pagination.total_pages = data.meta.last_page

                data_state.cities.is_failed = false
                data_state.cities.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cities.failure.catchError(error)

                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_failed = true
                }
            } finally {
                if (!data_state.cities.failure.error_state.is_cancel) {
                    data_state.cities.is_loading = false
                }
            }
        }

        const citiesLength = computed(() => state.cityIds.length)

        const addCity = (index: number) => state.cityIds.splice(index, 0, null)
        const deleteCity = (index: number) => state.cityIds.splice(index, 1)

        const getCityPlaceholder = (idx: number) => {
            if (idx === 0) {
                return 'Город отправки'
            } else if (idx === citiesLength.value - 1) {
                return 'Город прибытия'
            } else {
                return 'Город транзит'
            }
        }

        const isAddCity = (idx: number) => {
            return idx === citiesLength.value - 1
        }

        const fullFillDriverInfo = (data: DoDriverSearchData) => {
            state.full_name = data.fullName
            state.phone_number = data.phone
            state.brand = data.carBrand
            state.type = data.carType
            state.trailerLicenseNumber = data.trailerLicenseNumber
            state.identifyCard = data.identifyCard
            state.identifyCardDate = data.identifyCardDate
            state.licence = data.license
            state.licenceDate = data.licenseDate
            state.number = data.carNumber
        }

        const doDriverSearch = async () => {
            iin_form$.value.$touch()
            if (iin_form$.value.$invalid) return

            try {
                courier_state.is_loading = true
                courier_state.is_not_found = false

                const payload = {
                    iin: state.iin,
                }

                const { data } = await security.doDriverSearch(payload as DoDriverSearchParamsT)

                if (data) {
                    fullFillDriverInfo(data)
                } else {
                    courier_state.is_not_found = true
                }
            } catch (error) {
                courier_state.is_not_found = true
                notifications.notifyError({
                    error,
                    title: 'При проверке произошла ошибка',
                })
            } finally {
                courier_state.is_loading = false
            }
        }

        const onLoadContract = (carrier_id: number) => {
            const carrier = data_state.carriers.list.find((c) => c.id === carrier_id)

            if (!carrier || [2, 4].includes(carrier.status_id)) return
            const { bin, vehicleOwner } = carrier
            if (!vehicleOwner || !bin) return

            loadContract(bin)
        }

        const onCarrierChange = (carrier_id: number) => {
            loadCouriers(1)
            onLoadContract(carrier_id)

            if (carrier_id) {
                const carrier = data_state.carriers.list.find((c) => c.id === carrier_id)

                if (carrier) {
                    state.isNds = Number(carrier.is_nds)
                }
            } else {
                state.isNds = 0
            }
        }

        const onIsTripartiteChange = () => {
            state.tripartiteNumber = null
            state.tripartiteDate = null
        }

        const toggleCourier = () => {
            courier_state.is_opened = !courier_state.is_opened
        }

        const toggleCar = () => {
            car_state.is_opened = !car_state.is_opened
        }

        const onCarSelect = (id: number) => {
            const car_selected = data_state.cars.list.find((car) => car.id === id)

            if (car_selected) {
                state.brand = car_selected.model
                state.type = car_selected.vehicle_type_name
                state.number = car_selected.number
                state.carCapacityId = car_selected.ftl_car_type_id

                state.technicalPassport = car_selected.tech_passport_number
            }
        }

        const onCourierSelect = (id: number) => {
            const courier_selected = data_state.couriers.list.find((courier) => courier.id === id)

            if (courier_selected) {
                state.iin = courier_selected.iin
                state.full_name = courier_selected.fullName
                state.number = courier_selected.car ? courier_selected.car.number : null
                state.phone_number = courier_selected.phoneNumber
                if (courier_selected.car) {
                    state.carId = courier_selected.car.id
                    onCarSelect(state.carId)
                }

                if (courier_selected.license) {
                    state.identifyCard = courier_selected.license.identifyCardNumber
                    state.identifyCardDate = courier_selected.license.identifyCardIssueDate

                    state.licence = courier_selected.license.driverLicenseNumber
                    state.licenceDate = courier_selected.license.driverLicenseIssueDate
                }
            }
        }

        const setCourier = (response: DispatcherCourierT) => {
            state.courier_id = response.id
            onCourierSelect(response.id)

            if (response.license) {
                state.identifyCard = response.license.identifyCardNumber
                state.identifyCardDate = response.license.identifyCardIssueDate
                state.licence = response.license.driverLicenseNumber
                state.licenceDate = response.license.driverLicenseIssueDate
            }
        }

        const createCourier = async (payload: FormData) => {
            try {
                courier_state.is_loading = true

                const response = await couriers.createCourier(payload)

                await loadCouriers(1)
                toggleCourier()
                setCourier(response.data)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При создании водителя произошла ошибка',
                })
            } finally {
                courier_state.is_loading = false
            }
        }

        const setCar = async (response: CreateCouirerCar, files: File[]) => {
            try {
                await loadCars(1)

                state.carId = response.id
                state.technicalPassport = response.tech_passport_number
                state.carTechnicalPassportFiles = files

                onCarSelect(response.id)
            } catch (error) {
                console.error(error)
            }
        }

        const createCar = async (payload: FormData, files: File[]) => {
            try {
                car_state.is_loading = true

                const response = await couriers.createCar(payload)

                toggleCar()
                setCar(response.data, files)
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При создании автомобиля произошла ошибка',
                })
            } finally {
                car_state.is_loading = false
            }
        }

        //

        const emitCancel = () => {
            emit('cancel')
        }

        const setInfo = () => {
            if (!props.order) return

            state.dateOfLoad = props.order.take_date
            state.insurance = props.order.insurance
            state.temperatureRegime = props.order.temperature_mode
            state.costOfTransportation = props.order.cost_price.toString()

            const stockDeliveryNotesLast =
                props.order.stock_delivery_notes[props.order.stock_delivery_notes.length - 1]
            if (stockDeliveryNotesLast && stockDeliveryNotesLast.receiver) {
                state.receiverFullName = stockDeliveryNotesLast.receiver.full_name
                state.receiverPhoneNumber = stockDeliveryNotesLast.receiver.phone
                state.unloadAddress = stockDeliveryNotesLast.receiver.address

                state.cityIds[state.cityIds.length - 1] = stockDeliveryNotesLast.receiver.city_id
            }

            if (props.order.sender) {
                state.senderFullName = props.order.sender.full_name
                state.senderPhoneNumber = props.order.sender.phone
                state.loadAddress = props.order.sender.full_address

                state.cityIds[0] = props.order.sender.city_id
            }
        }

        const loadCars = async (page: number, search: string | null = null) => {
            data_state.cars.pagination.current_page = page

            try {
                data_state.cars.is_loading = true

                const response = await couriers.getCars({
                    page,
                    number: search,
                })

                data_state.cars.list = response.data
                data_state.cars.pagination.total_pages = response.meta.last_page

                data_state.cars.is_failed = false
            } catch (error) {
                data_state.cars.is_failed = true
            } finally {
                data_state.cars.is_loading = false
            }
        }

        const loadMoreCars = async (page: number) => {
            if (page > data_state.cars.pagination.total_pages) return
            data_state.cars.pagination.current_page = page

            try {
                data_state.cars.is_loading = true

                const response = await couriers.getCars({
                    page,
                })
                data_state.cars.list = data_state.cars.list.concat(response.data)
                data_state.cars.pagination.total_pages = response.meta.last_page

                data_state.cars.is_failed = false
                data_state.cars.is_failed = false
            } catch (error) {
                data_state.cars.is_failed = true
            } finally {
                data_state.cars.is_loading = false
            }
        }

        const searchCars = (search_query: string) => {
            loadCars(1, search_query)
        }

        const unshiftFindedCar = async (car: CouirerCarT) => {
            await loadCars(data_state.cars.pagination.current_page)
            data_state.cars.list.unshift(car)
        }

        const onSubmitCost = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid || !state.comment) return

            state.is_cost = false
            addBid()
        }

        onBeforeMount(() => {
            loadCarriers(1)
            loadCities(1)
            loadCompanies()
            loadCars(1)

            setInfo()
        })

        // computed IIN

        const isNotFoundIIN = computed(
            () => sb_state_iin.is_rejected === null && sb_state_iin.is_request_completed
        )
        const isRejectedIIN = computed(
            () => sb_state_iin.is_rejected && sb_state_iin.is_request_completed
        )
        const isNotCheckedIIN = computed(
            () => !sb_state_iin.is_checked && sb_state_iin.is_request_completed
        )

        // computed Car

        const isRejectedCar = computed(
            () => sb_state_car.is_rejected && sb_state_car.is_request_completed
        )
        const isNotCheckedCar = computed(
            () => !sb_state_car.is_checked && sb_state_car.is_request_completed
        )

        //

        const isDelete = (index: number) =>
            index > 0 && index < state.cityIds.length - 1 && state.cityIds.length > 2

        //

        return {
            state,
            courier_state,
            car_state,
            sb_state_iin,
            sb_state_car,
            state_form$,
            iin_form$,
            data_state,
            emitCancel,
            doDriverSearch,
            loadCompanies,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            onCarrierChange,
            onIsTripartiteChange,
            deleteCity,
            //
            loadCouriers,
            searchCouriers,
            unshiftFindedCourier,
            //
            loadCities,
            addCity,
            getCityPlaceholder,
            isAddCity,
            //
            addBid,
            resetForm,
            //
            termAndFormOfPayment,
            // iin computed
            isRejectedIIN,
            isNotCheckedIIN,
            isNotFoundIIN,
            // car computed
            isRejectedCar,
            isNotCheckedCar,
            //
            citiesLength,
            isDelete,
            createCourier,
            toggleCourier,
            createCar,
            toggleCar,
            searchCars,
            unshiftFindedCar,
            setCar,
            loadMoreCars,
            onCourierSelect,
            loadCars,
            onCarSelect,
            onSubmitCost,
            onAddBid,
            //
            PHONE_NUMBER_MASK,
        }
    },
})
